/**
 * Webメールの SSO ログイン画面（`/webmail-login?rid=...`・**認証不要**）。
 *
 * ## 位置づけ
 *
 * CP は Webメール（aurora-mail）に対する OIDC の identity provider として
 * 動く。`GET /api/v1/public/oidc/authorize` は画面を持たず、認可リクエストを
 * Redis へ置いて**この画面へ 302 で送る**。ここで資格情報を受け取り、
 * `POST /api/v1/public/oidc/login` に渡して認可コードを取り、
 * RP（Webメール）へ戻す。
 *
 * API に HTML を持たせず SPA の 1 ページにしているのは、CSP・テーマ・
 * ブランディングを二重に持たないため（SPA 側は既に全部持っている）。
 *
 * ## ここで入れるのは「メールアドレスのパスワード」
 *
 * コントロールパネルのログイン（契約者の管理アカウント）とは**別物**。
 * 照合先は `mailboxes.password_hash` で、メールソフトに入れるものと同じ。
 * 取り違えが最も多い場所なので、画面でも繰り返し断っている。
 *
 * ## 守っていること
 *
 *   1. **`rid` をフォームの値として持たない。** URL から読み、送信にだけ使う。
 *   2. **失敗の理由を区別しない。** サーバー側も「アドレスが無い」と
 *      「パスワードが違う」を出し分けない（実在の総当たりを許さない）。
 *   3. 成功したら `redirectTo` へ**そのまま遷移する**。値はサーバーが
 *      登録済み redirect_uri から組み立てたもので、画面は組み立てない。
 */
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
} from '@mail-sv/ui';
import { Mail } from 'lucide-react';
import { publicApi } from '../lib/api.js';
import { FormAlert } from '../components/form.js';
import { useSearchParams } from '../lib/nav.js';
import { env } from '../env.js';

export function WebmailLoginPage(): React.JSX.Element {
  const search = useSearchParams();
  const rid = typeof search.rid === 'string' ? search.rid : '';

  const [address, setAddress] = React.useState('');
  const [password, setPassword] = React.useState('');

  const submit = useMutation({
    mutationFn: () =>
      publicApi.post<{ redirectTo: string }>('/oidc/login', { rid, address, password }),
    onSuccess: (res) => {
      /*
       * **`assign` で遷移する。** RP のドメインへ出ていくので SPA の
       * ルーターは使えない。`replace` にしないのは、戻るで再試行できる
       * ようにするため（コードは使い捨てなので戻っても再利用はされない）。
       */
      window.location.assign(res.redirectTo);
    },
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <div className="brand-gradient mx-auto flex size-12 items-center justify-center rounded-xl text-white shadow-lg shadow-primary/25">
            <Mail className="size-6" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold">{env.brandName}</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Webメールにログイン</CardTitle>
            <CardDescription>
              メールアドレスと、そのアドレスのパスワードを入力してください。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {rid === '' ? (
              /*
               * `rid` が無い＝Webメールからの導線を通っていない。
               * ここで入力させても認可リクエストが無いので必ず失敗する。
               */
              <Alert variant="warning">
                <AlertTitle>Webメールからやり直してください</AlertTitle>
                <AlertDescription>
                  この画面は Webメールのログインボタンから開きます。直接開くと
                  ログインできません。
                </AlertDescription>
              </Alert>
            ) : (
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  submit.mutate();
                }}
                noValidate
              >
                <FormAlert error={submit.error} />

                <Alert variant="info">
                  <AlertDescription>
                    ここで入力するのは<strong>メールアドレスのパスワード</strong>
                    （メールソフトに設定するもの）です。コントロールパネルの
                    ログインとは別のものです。
                  </AlertDescription>
                </Alert>

                <Field label="メールアドレス" htmlFor="wm-address" required>
                  <Input
                    id="wm-address"
                    name="username"
                    type="email"
                    autoComplete="username"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="info@example.jp"
                    autoFocus
                  />
                </Field>

                <Field label="パスワード" htmlFor="wm-password" required>
                  <Input
                    id="wm-password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Field>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={submit.isPending || address === '' || password === ''}
                >
                  {submit.isPending ? 'ログインしています…' : 'ログイン'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
